// src/pages/Production/RawMaterialStockReport.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import ReportGenerator from '../../components/production/ReportGenerator';
import { generateRawMaterialStockReport } from '../../utils/reportingUtils';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b',
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e2e8f0'
    }
};

function RawMaterialStockReport() {
    const { userData } = useAuth();
    const [rawMaterials, setRawMaterials] = useState([]);
    const [products, setProducts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch real data from Firebase
    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Fetch raw materials
        const unsubRawMaterials = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterials')),
            snap => setRawMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch products (for those marked as raw materials)
        const unsubProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')),
            snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch warehouses
        const unsubWarehouses = onSnapshot(
            query(collection(db, 'companies', companyId, 'warehouses')),
            snap => {
                setWarehouses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }
        );

        return () => {
            unsubRawMaterials();
            unsubProducts();
            unsubWarehouses();
        };
    }, [userData]);

    // Process data for report
    const reportData = useMemo(() => {
        // Combine raw materials from both sources
        const allRawMaterials = [
            ...rawMaterials.map(rm => ({
                id: rm.id,
                name: rm.name,
                code: rm.barcode || rm.code || '',
                unit: rm.unit || 'Adet',
                quantity: rm.quantity || 0,
                criticalLevel: rm.criticalLevel || rm.minStock || 0,
                warehouseQuantities: rm.warehouseQuantities || {}
            })),
            ...products.filter(p => p.isRawMaterial).map(p => ({
                id: p.id,
                name: p.name,
                code: p.barcode || p.code || '',
                unit: p.unit || 'Adet',
                quantity: p.quantity || 0,
                criticalLevel: p.criticalLevel || p.minStock || 0,
                warehouseQuantities: p.warehouseQuantities || {}
            }))
        ];

        if (allRawMaterials.length === 0) return null;

        // Create warehouses list
        const warehouseList = warehouses.length > 0
            ? warehouses.map(w => ({ id: w.id, name: w.name, isActive: w.isActive !== false }))
            : [{ id: 'default', name: 'Ana Depo', isActive: true }];

        return generateRawMaterialStockReport(allRawMaterials, warehouseList);
    }, [rawMaterials, products, warehouses]);

    const totalMaterials = rawMaterials.length + products.filter(p => p.isRawMaterial).length;

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                        <p>Hammadde stokları yükleniyor...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/raporlar" style={styles.backLink}>
                    ← Üretim Raporları
                </Link>
                <div>
                    <h1 style={styles.title}>Hammadde Stok Durumu</h1>
                    <p style={{ color: '#64748b', marginTop: '4px' }}>
                        Hammadde stok seviyeleri ve kritik stok uyarıları
                        {totalMaterials > 0 && (
                            <span style={{ marginLeft: '10px' }}>
                                ({totalMaterials} hammadde)
                            </span>
                        )}
                    </p>
                </div>
            </div>

            {!reportData || totalMaterials === 0 ? (
                <div style={styles.emptyState}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                    <h3 style={{ color: '#374151', marginBottom: '8px' }}>Hammadde Bulunamadı</h3>
                    <p>Henüz hammadde tanımlanmamış.</p>
                    <Link
                        to="/stok"
                        style={{
                            display: 'inline-block',
                            marginTop: '20px',
                            padding: '12px 24px',
                            background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
                            color: 'white',
                            borderRadius: '12px',
                            textDecoration: 'none',
                            fontWeight: '600'
                        }}
                    >
                        Ürünlere Git
                    </Link>
                </div>
            ) : (
                <ReportGenerator
                    reportType="raw-material-stock"
                    reportData={reportData}
                    onExport={(format) => console.log(`Exporting as ${format}`)}
                />
            )}
        </div>
    );
}

export default RawMaterialStockReport;